import { styled } from '@mui/system';
import { Checkbox } from '@src/common/components/Checkbox';
import {
  formatDimensionValueForDisplay,
  formatWeightValueForDisplay,
} from '@src/common/components/FormatWeightValueForDisplay';
import { Paper } from '@src/common/components/Paper';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import { UpdatableTableCell } from '@src/orders/components/UpdatableTableCell';
import type { Order } from '@src/orders/domain/Order';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import {
  AxleCapacityField,
  AxleGroupField,
  AxleSpacingField,
  AxleTypeField,
  AxleWeightField,
  AxleWidthField,
  IsSteeringAxleField,
  SuspensionTypeField,
  TireCountField,
  TireRatingField,
  TireSizeField,
} from '@src/permitField/AxleInformationFields';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type TableProps = {
  name: string;
  children: ReactNode;
  fields: string[];
  isMetric: boolean;
  order: Order;
};

export const ReadOnlyAxlesTable = ({
  name,
  children,
  fields,
  isMetric,
  order,
}: TableProps) => {
  const { t } = useTranslation();
  const measurementUnit = isMetric
    ? t('units.meters')
    : t('units.feetAndInches');
  const weightUnit = isMetric ? t('units.kilos') : t('units.pounds');

  return (
    <Paper>
      <Table aria-label={name} sx={{ minWidth: 650 }} padding="none">
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
              {t('axleNumberHeader')}
            </StyledTableCell>
            <ConditionalField
              conditions={AxleTypeField.fieldName}
              fields={fields}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleTypeField.fieldName, order)}
              >
                {t(`fields.${AxleTypeField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={AxleWeightField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleWeightField.fieldName, order)}
              >
                {t(`fields.${AxleWeightField.fieldName}`) + ` (${weightUnit})`}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={AxleSpacingField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleSpacingField.fieldName, order)}
              >
                {t(`fields.${AxleSpacingField.fieldName}`) +
                  ` (${measurementUnit})`}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={TireCountField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(TireCountField.fieldName, order)}
              >
                {t(`fields.${TireCountField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={TireSizeField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(TireSizeField.fieldName, order)}
              >
                {t(`fields.${TireSizeField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={TireRatingField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(TireRatingField.fieldName, order)}
              >
                {t(`fields.${TireRatingField.fieldName}`) + ` (${weightUnit})`}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={AxleCapacityField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleCapacityField.fieldName, order)}
              >
                {t(`fields.${AxleCapacityField.fieldName}`) +
                  ` (${weightUnit})`}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={AxleWidthField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleWidthField.fieldName, order)}
              >
                {t(`fields.${AxleWidthField.fieldName}`) +
                  ` (${measurementUnit})`}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={AxleGroupField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(AxleGroupField.fieldName, order)}
              >
                {t(`fields.${AxleGroupField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={SuspensionTypeField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(
                  SuspensionTypeField.fieldName,
                  order,
                )}
              >
                {t(`fields.${SuspensionTypeField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
            <ConditionalField
              fields={fields}
              conditions={IsSteeringAxleField.fieldName}
            >
              <UpdatableTableCell
                lastUpdated={getLastUpdated(
                  IsSteeringAxleField.fieldName,
                  order,
                )}
              >
                {t(`fields.${IsSteeringAxleField.fieldName}`)}
              </UpdatableTableCell>
            </ConditionalField>
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
        <TableFooter></TableFooter>
      </Table>
    </Paper>
  );
};

export const ReadOnlyAxlesRow = ({
  axle,
  axleIndex,
  fields,
  isMetric,
}: {
  axleIndex: number;
  fields: string[];
  axle: Record<string, string | number>;
  isMetric: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <TableRow
      key={axleIndex}
      aria-label={t('axleNumber', { number: axleIndex + 1 })}
    >
      <StyledTableCell align="center" sx={{ verticalAlign: 'middle' }}>
        <Typography>{axleIndex + 1}</Typography>
      </StyledTableCell>
      <ConditionalField fields={fields} conditions={AxleTypeField.fieldName}>
        <StyledTableCell>{axle[AxleTypeField.fieldName]}</StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleWeightField.fieldName}>
        <StyledTableCell>
          {formatWeightValueForDisplay(
            axle[AxleWeightField.fieldName],
            isMetric,
          )}
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleSpacingField.fieldName}>
        <StyledTableCell>
          {/* Tractors never have any spacing for the first axle so the field is hidden */}
          {formatDimensionValueForDisplay(
            axle[AxleSpacingField.fieldName],
            isMetric,
          )}
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireCountField.fieldName}>
        <StyledTableCell>{axle[TireCountField.fieldName]}</StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireSizeField.fieldName}>
        <StyledTableCell>{axle[TireSizeField.fieldName]}</StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireRatingField.fieldName}>
        <StyledTableCell>
          {formatWeightValueForDisplay(
            axle[TireRatingField.fieldName],
            isMetric,
          )}
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={AxleCapacityField.fieldName}
      >
        <StyledTableCell>
          {formatWeightValueForDisplay(
            axle[AxleCapacityField.fieldName],
            isMetric,
          )}
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleWidthField.fieldName}>
        <StyledTableCell id={AxleWidthField.fieldName}>
          {formatDimensionValueForDisplay(
            axle[AxleWidthField.fieldName],
            isMetric,
          )}
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleGroupField.fieldName}>
        <StyledTableCell>{axle[AxleGroupField.fieldName]}</StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={SuspensionTypeField.fieldName}
      >
        <StyledTableCell>{axle[SuspensionTypeField.fieldName]}</StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={IsSteeringAxleField.fieldName}
      >
        <CheckboxTableCell>
          <Checkbox
            label=""
            hideLabel
            disabled={true}
            checked={Boolean(axle[IsSteeringAxleField.fieldName])}
          />
        </CheckboxTableCell>
      </ConditionalField>
    </TableRow>
  );
};

const CheckboxTableCell = styled(TableCell)`
  & > div {
    min-width: 80px;
    width: 100%;
    align-items: center;
  }
`;

const StyledTableCell = styled(TableCell)`
  padding: ${theme.spacing['2']} ${theme.spacing['1']};
`;

const getLastUpdated = (fieldName: string, order: Order) => {
  const lastUpdatedDate = order.getLastUpdatedFor(fieldName);
  return lastUpdatedDate
    ? new Timestamp(lastUpdatedDate).formatUTCDate()
    : undefined;
};
